import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import { Schedule } from "@iventis/domain-model/model/schedule";
import qs from "qs";
import { AxiosResponse } from "axios";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { ExportUrl } from "@iventis/domain-model/model/exportUrl";
import { parseISO } from "date-fns";
import { aggregatePagedResponse } from "@iventis/api-helpers/src/api-helpers";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { FilterFormat } from "@iventis/types/api.types";
import { EventBlockDone } from "@iventis/domain-model/model/eventBlockDone";
import { ExportDcasRequest } from "@iventis/domain-model/model/exportDcasRequest";
import { DataField } from "@iventis/domain-model/model/dataField";
import { scheduleApi, libraryApi, exportsApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { aggregateHierarchy } from "./hierarchy.helpers";
import { ScheduleViewBySelection } from "./view-by-selector";
import { FilterType } from "./filter-rules";
import { flattenEventBlockHierarchy, removeNullDataFieldValuesFromEventBlock, checkForNecessarySubEventChanges } from "./schedule-helper-functions";
import { hasEventBlockUpdated, mapSessionFormValuesToEventBlock } from "./event-block-helpers";
import { ScheduleVersionNode } from "./schedule.types";
import { CreateSessionValues, UpdateSessionValues } from "./session.types";

export const exportUrlsKey = "export-urls";

export function getSchedule() {
    return scheduleApi.get<Schedule>(`/schedules`).then((res) => res.data);
}

export function updateSchedule(schedule: Schedule) {
    return scheduleApi.post<Schedule>(`/schedules`, schedule, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.schedule.schedule) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.schedule.schedule) }),
        },
    });
}

export function getScheduleData(scheduleId: string): Promise<{ scheduleNode: Node; venueLocations: Hierarchy<VenueLocation>[]; availableSchedules: Hierarchy<ScheduleVersion> }> {
    const scheduleNode = getScheduleNode(scheduleId, [NodeType.Schedule, NodeType.ScheduleVersion]);
    const venueLocations = getVenueLocations(scheduleId);
    const availableSchedules = getScheduleVersionsInSchedule(scheduleId);
    return Promise.all([scheduleNode, venueLocations, availableSchedules]).then((res) => ({ scheduleNode: res[0], venueLocations: res[1], availableSchedules: res[2] }));
}

export async function getScheduleNode(scheduleId: string, nodeTypeFilter: NodeType[]) {
    let queryStringNodeTypeFilter;
    if (nodeTypeFilter != null) {
        queryStringNodeTypeFilter = {
            params: { nodeTypeFilter },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        };
    }
    return libraryApi.get<Node>(`/nodes/${scheduleId}`, queryStringNodeTypeFilter).then((res) => res.data);
}

export function getScheduleVersionData(scheduleVersionId: string): Promise<{ scheduleVersionNode: Node; scheduleVersionHierarchy: Hierarchy<ScheduleVersion> }> {
    const scheduleVersionNode = getScheduleNode(scheduleVersionId, [NodeType.ScheduleVersion]);
    const scheduleVersionHierarchy = getScheduleVersionHierarchy(scheduleVersionId);
    return Promise.all([scheduleVersionNode, scheduleVersionHierarchy]).then((res) => ({
        scheduleVersionNode: res[0],
        scheduleVersionHierarchy: res[1],
    }));
}

export function getScheduleVersionHierarchy(scheduleVersionId: string): Promise<Hierarchy<ScheduleVersion>> {
    return scheduleApi.get<Hierarchy<ScheduleVersion>>(`schedule-versions/hierarchy/${scheduleVersionId}`).then((res) => res.data);
}

export function getScheduleVersionsInSchedule(scheduleId: string): Promise<Hierarchy<ScheduleVersion>> {
    const formattedFilter: FilterFormat[] = [
        {
            fieldName: "type",
            operator: IventisFilterOperator.Eq,
            value: "Schedule",
        },
    ];

    return scheduleApi
        .get<Hierarchy<ScheduleVersion>>(`schedule-versions/scheduleId/${scheduleId}`, {
            params: { filter: JSON.stringify(formattedFilter) },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        })
        .then((res) => res.data);
}

export function getVenueLocations(scheduleId: string): Promise<Hierarchy<VenueLocation>[]> {
    return scheduleApi.get<Hierarchy<VenueLocation>[]>(`/venue-locations/${scheduleId}/hierarchy`).then((res) => res.data);
}

export function createVenueLocation(venueLocation: VenueLocation): Promise<VenueLocation> {
    return scheduleApi
        .post<VenueLocation>("/venue-locations", venueLocation, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
            },
        })
        .then((res) => res.data);
}

export function deleteVenueLocation(id: string): Promise<VenueLocation> {
    return scheduleApi
        .delete<VenueLocation>(`/venue-locations/${id}`, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
                message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
            },
        })
        .then((res) => res.data);
}

export function editVenueLocation(venueLocation: VenueLocation): Promise<VenueLocation> {
    return scheduleApi
        .put<VenueLocation>(`/venue-locations/${venueLocation.id}`, venueLocation, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.schedule.add_data_fields_form.venue_location) }),
            },
        })
        .then((res) => res.data);
}

// Converts event block hierarchy returned from the API into proper javascript date objects, to be used correctly elsewhere in the application
export const convertEventBlockHierarchyDates = (eventBlockHierarchy: Hierarchy<EventBlock>) => {
    convertEventBlockDates(eventBlockHierarchy.item);
    eventBlockHierarchy.children.forEach((children) => convertEventBlockHierarchyDates(children));
};

// Converts event block returned from the API into proper javascript date objects, to be used correctly elsewhere in the application
const convertEventBlockDates = (eventBlock: EventBlock) => {
    /* eslint-disable no-param-reassign */
    eventBlock.startAt = eventBlock.startAt != null ? parseISO(eventBlock.startAt.toString()) : undefined;
    eventBlock.endAt = eventBlock.endAt != null ? parseISO(eventBlock.endAt.toString()) : undefined;
    eventBlock.zeroAt = eventBlock.zeroAt != null ? eventBlock.zeroAt : undefined;

    eventBlock.relativeEventRules?.forEach((rel) => {
        rel.startAt = rel.startAt != null ? parseISO(rel.startAt.toString()) : null;
        rel.endAt = rel.startAt != null ? parseISO(rel.startAt.toString()) : null;
        rel.recurringEventRules?.forEach((rec) => {
            rec.startOfRecurrence = rec.startOfRecurrence != null ? parseISO(rec.startOfRecurrence.toString()) : null;
            rec.endOfRecurrence = rec.endOfRecurrence != null ? parseISO(rec.endOfRecurrence.toString()) : null;
            rec.recurringDates = rec.recurringDates != null ? rec.recurringDates.map((date) => parseISO(date.toString())) : null;
        });
    });

    eventBlock.recurringEventRules?.forEach((rec) => {
        rec.startOfRecurrence = rec.startOfRecurrence != null ? parseISO(rec.startOfRecurrence.toString()) : null;
        rec.endOfRecurrence = rec.endOfRecurrence != null ? parseISO(rec.endOfRecurrence.toString()) : null;
        rec.recurringDates = rec.recurringDates != null ? rec.recurringDates.map((date) => parseISO(date.toString())) : null;
    });
};

export function getHierarchyEventBlocks(
    scheduleVersionId: string,
    viewByOption: ScheduleViewBySelection,
    selectedFilters: { day: Date; venues: Hierarchy<VenueLocation>[]; dataFieldSelection: DataFieldListItem[] }
) {
    const formattedFilter: FilterFormat[] = [];

    if (viewByOption.filterType === FilterType.DayVenue || viewByOption.filterType === FilterType.DayDataField || viewByOption.filterType === FilterType.VenueRunSheet) {
        formattedFilter.push({
            fieldName: "date",
            operator: IventisFilterOperator.Eq,
            value: selectedFilters.day,
        });
    }

    if (viewByOption.filterType === FilterType.DayDataField) {
        formattedFilter.push({
            fieldName: viewByOption.dataField.name,
            operator: IventisFilterOperator.In,
            value: selectedFilters.dataFieldSelection.map((item) => item.id),
        });
    }

    if (
        viewByOption.filterType === FilterType.Venue ||
        viewByOption.filterType === FilterType.DayVenue ||
        viewByOption.filterType === FilterType.Summary ||
        viewByOption.filterType === FilterType.VenueRunSheet
    ) {
        formattedFilter.push({
            fieldName: "venueIds",
            operator: IventisFilterOperator.In,
            value: selectedFilters.venues.map((venue) => venue.item.id),
        });
    }

    if (viewByOption.filterType === FilterType.DataField) {
        formattedFilter.push({
            fieldName: viewByOption.dataField.name,
            operator: IventisFilterOperator.Eq,
            value: selectedFilters.dataFieldSelection[0].id,
        });
    }

    switch (viewByOption.filterType) {
        case FilterType.VenueRunSheet:
            // Do not add the calendar level for venue run sheet as we need all event blocks
            break;
        case FilterType.Summary:
            // Add a filter to only show level 1 (session) event blocks
            formattedFilter.push({
                fieldName: "calendarLevel",
                operator: IventisFilterOperator.In,
                value: [1],
            });
            break;
        default:
            // Add a filter to only show level 1 (session) and 2 (schedule item) event blocks
            formattedFilter.push({
                fieldName: "calendarLevel",
                operator: IventisFilterOperator.In,
                value: [1, 2],
            });
            break;
    }
    return aggregatePagedResponse<Hierarchy<EventBlock>>(scheduleApi, `/event-blocks/schedule-versions/${scheduleVersionId}?`, {}, aggregateHierarchy, (url, config) =>
        scheduleApi.post(url, formattedFilter ?? [], config)
    ).then((hierarchy: Hierarchy<EventBlock>) => {
        convertEventBlockHierarchyDates(hierarchy);
        return hierarchy;
    });
}

export const getEventBlockSingleHierarchy = async (id: string): Promise<Hierarchy<EventBlock>> => {
    const res = (await aggregatePagedResponse(scheduleApi, `/event-blocks/${id}/hierarchy`, {}, aggregateHierarchy)) as Hierarchy<EventBlock>;
    convertEventBlockHierarchyDates(res);
    return res;
};

export async function deleteScheduleVersion(scheduleVersionId: string) {
    await scheduleApi.delete(`/schedule-versions/${scheduleVersionId}`, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.schedule.schedule) }),
            message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.schedule.schedule) }),
        },
    });
    return scheduleVersionId;
}

/**
 * Left here in case it is re-introduced in future (creating sub-sessions in the same request as updating a session)
 * @deprecated
 * */
export const putEventBlock = async (eventBlockHierarchy: Hierarchy<EventBlock>): Promise<EventBlockPutResponse> => {
    const results = await scheduleApi.put<Hierarchy<EventBlock>, AxiosResponse<EventBlockPutResponse>>(
        "/event-blocks",
        removeNullDataFieldValuesFromEventBlock(eventBlockHierarchy),
        {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockHierarchy.item.type.calendarLevel ?? "activity"]),
                }),
                message: translate(Content.errors.custom_network_error.update.message, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockHierarchy.item.type.calendarLevel ?? "activity"]),
                }),
            },
        }
    );
    results.data.updated.forEach(async (e) => {
        if (e.venueLocation != null && e.id === eventBlockHierarchy.item.id) {
            e.venueLocation = eventBlockHierarchy.item.venueLocation;
        }
        convertEventBlockDates(e);
    });
    results.data.created.forEach((e) => convertEventBlockDates(e));
    return results.data;
};

export const patchEventBlock = async (id: string, eventBlock: Partial<EventBlock>, type: ScheduleConfigEventBlockType) => {
    const result = await scheduleApi.patch<EventBlock>(`/event-blocks/${id}`, eventBlock, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, {
                entity: translate(Content.settings.scheduleConfig.calendarLevel[type.calendarLevel ?? "activity"]),
            }),
            message: translate(Content.errors.custom_network_error.update.message, {
                entity: translate(Content.settings.scheduleConfig.calendarLevel[type.calendarLevel ?? "activity"]),
            }),
        },
    });
    return result.data;
};

/**
 * Updates an event block using patch route. Has the ability to update child events too such as resetting a child venue to null if necessary.
 * Top level event block param is required because changes to events with relative rules will require the hierarchy to be recalculated using the get hierarchy route
 * */
export const updateEventBlock = async (
    existingEventBlock: EventBlock,
    newEventBlockAsHierarchy: Hierarchy<EventBlock>,
    topLevelEventBlockId: string,
    includeResponse = true
): Promise<EventBlockPutResponse> => {
    const newEventBlock = removeNullDataFieldValuesFromEventBlock(newEventBlockAsHierarchy).item;
    const eventsToPatch: Record<string, Partial<EventBlock>> = { [existingEventBlock.id]: {} };
    const hasUpdated = hasEventBlockUpdated(existingEventBlock, newEventBlock, (keyOfEventBlock) => {
        // Callback each time a property is registered as updated
        eventsToPatch[existingEventBlock.id][keyOfEventBlock as string] = newEventBlock[keyOfEventBlock];
        switch (keyOfEventBlock) {
            case "venueLocation":
                newEventBlockAsHierarchy.children.forEach((child) => {
                    // If a session changes it's venue, it's children must reset their locations
                    eventsToPatch[child.item.id] = { venueLocation: null };
                });
                break;
            case "relativeEventRules":
                if (!(newEventBlock.relativeEventRules?.length > 0) && newEventBlock.startAt) {
                    // Reaches here if event block has switched from relative rule to fixed time. Thus, we have to send down the start/end/zero times, as previously they would be null in the db
                    eventsToPatch[existingEventBlock.id].startAt = newEventBlock.startAt;
                    eventsToPatch[existingEventBlock.id].endAt = newEventBlock.endAt;
                    eventsToPatch[existingEventBlock.id].zeroAt = newEventBlock.zeroAt;
                }
                break;
            default:
                break;
        }
    });

    // Set a default empty response
    const response = { updated: [], created: [], deleted: [] };

    if (hasUpdated) {
        // Patch each updated entity (unless venue has changed on a top level event block, then this should only ever be one patch request)
        await Promise.all(Object.entries(eventsToPatch).map(([id, patchObject]) => patchEventBlock(id, patchObject, existingEventBlock.type)));

        // Only perform the below fetch (getEventBlockHierarchyWithEventBlockId) if the code that uses this function requires a response (hence `includeResponse` parameter)
        if (includeResponse) {
            // In case we have relative rules we must reconstruct the (virtual) hierarchy so we have correct start/end times. We do this by re-fetching the hierarchy
            const updatedHierarchy = await getEventBlockHierarchyWithEventBlockId(topLevelEventBlockId, newEventBlock.startAt);

            // Append all the updated event blocks to the reponse
            response.updated = flattenEventBlockHierarchy(updatedHierarchy);
        }
    }
    return response;
};

/** Updates an event blocks' done status per date/per related event block */
export const updateEventBlockDone = async (eventBlockDone: EventBlockDone, nodeType: ScheduleConfigEventBlockType) => {
    if (eventBlockDone.done) {
        await scheduleApi.post<void, AxiosResponse<void, EventBlockDone>>("event-block-done", eventBlockDone, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[nodeType.calendarLevel ?? "activity"]),
                }),
                message: translate(Content.errors.custom_network_error.update.message, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[nodeType.calendarLevel ?? "activity"]),
                }),
            },
        });
    } else {
        await scheduleApi.delete("event-block-done", {
            params: eventBlockDone,
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[nodeType.calendarLevel ?? "activity"]),
                }),
                message: translate(Content.errors.custom_network_error.update.message, {
                    entity: translate(Content.settings.scheduleConfig.calendarLevel[nodeType.calendarLevel ?? "activity"]),
                }),
            },
        });
    }
};

/** Gets the event block done statuses for the given venue and date */
export const getEventBlocksDone = async (params: { day: Date; venueLocationId: string; scheduleVersionId: string }) => {
    const res = await scheduleApi.get<EventBlockDone[]>("event-block-done", { params });
    return res.data;
};

export const createEventBlock = async (eventBlockHierarchy: Hierarchy<EventBlock>): Promise<Hierarchy<EventBlock>> => {
    const results = await scheduleApi.post<Hierarchy<EventBlock>>("/event-blocks", removeNullDataFieldValuesFromEventBlock(eventBlockHierarchy), {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.create.title, {
                entity: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockHierarchy.item.type.calendarLevel ?? "activity"]),
            }),
            message: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockHierarchy.item.type.calendarLevel ?? "activity"]),
        },
    });
    convertEventBlockHierarchyDates(results.data);
    return occupyVenueLocation(eventBlockHierarchy, results.data);
};

export const deleteEventBlock = async (id: string, eventBlockType: { calendarLevel?: number }): Promise<void> => {
    await scheduleApi.delete(`/event-blocks/${id}`, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.delete_error.title, {
                entity: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockType.calendarLevel ?? "activity"]),
            }),
            message: translate(Content.errors.custom_network_error.delete_error.message, {
                entity: translate(Content.settings.scheduleConfig.calendarLevel[eventBlockType.calendarLevel ?? "activity"]),
            }),
        },
    });
};

export const getEventBlockHierarchyWithEventBlockId = async (eventBlockId: string, dateFilter?: Date) => {
    const filter = [];
    if (dateFilter != null) {
        filter.push({
            fieldName: "date",
            operator: IventisFilterOperator.Eq,
            value: dateFilter,
        });
    }
    const updatedHierarchy = (await aggregatePagedResponse<Hierarchy<EventBlock>>(
        scheduleApi,
        `/event-blocks/${eventBlockId}/hierarchy`,
        {
            params: { filter: JSON.stringify(filter) },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        },
        aggregateHierarchy
    )) as Hierarchy<EventBlock>;
    convertEventBlockHierarchyDates(updatedHierarchy);
    return updatedHierarchy;
};

const occupyVenueLocation = (eventRequest: Hierarchy<EventBlock>, eventResponse: Hierarchy<EventBlock>) => ({
    ...eventResponse,
    item: { ...eventResponse.item, venueLocation: eventRequest.item.venueLocation },
});

export const getExportUrls = async () => {
    const exportUrls = await exportsApi.get<ExportUrl[]>("/urls");
    return exportUrls.data;
};

export const exportScheduleVersionToUrl = (scheduleVersionId: string, exportUrlId: string) =>
    scheduleApi.post("/odf/export", {
        scheduleVersionId,
        exportUrlId,
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.export_error.title, { entity: translate(Content.schedule.schedule) }),
            message: translate(Content.errors.custom_network_error.export_error.message, { entity: translate(Content.schedule.schedule) }),
        },
    });

export const createScheduleVersionSharedLink = (scheduleVersionId: string): Promise<string> =>
    scheduleApi
        .post(`/shared-links/schedule-versions/${scheduleVersionId}`, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, {
                    action: translate(Content.schedule3.shareOrExport.shareLinkOptionTitle)?.toLocaleLowerCase?.(),
                }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.schedule3.shareOrExport.shareLinkOptionTitle) }),
            },
        })
        .then((response) => response.data);

export function updateScheduleVersionName(scheduleVersionId: string, scheduleVersionName: string) {
    return scheduleApi.patch<ScheduleVersionNode>(`/schedule-versions/${scheduleVersionId}`, {
        name: scheduleVersionName,
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.schedule.schedule) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.schedule.schedule) }),
        },
    });
}

export const exportRunSheetToExcel = (scheduleVersionId: string, venueLocationId: string, day?: Date) => {
    const req: ExportDcasRequest = {
        day: day ?? null,
        venueLocationId,
        scheduleVersionId,
        export: undefined, // Not needed, defined in route
    };

    scheduleApi.post<ExportDcasRequest>(`/schedule-versions/${scheduleVersionId}/dcas-exports`, req, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.runsheet.actions.export_as_excel)?.toLocaleLowerCase?.() }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.runsheet.actions.export_as_excel) }),
        },
    });
};

export const createSession = async (createSessionValues: CreateSessionValues, dataFields: DataField[]): Promise<Hierarchy<EventBlock>> => {
    const { sessionCodeDataField, selectedEvents } = createSessionValues;
    const eventBlock: Hierarchy<EventBlock> = {
        item: mapSessionFormValuesToEventBlock(createSessionValues, undefined, sessionCodeDataField, createSessionValues.timezone, dataFields),
        children: selectedEvents as Hierarchy<EventBlock>[],
    };
    const res = await createEventBlock(eventBlock);
    return res;
};

export type EventBlockPutResponse = {
    created: EventBlock[];
    updated: EventBlock[];
    deleted: EventBlock[];
};

export const editSession = async (updateSessionValues: UpdateSessionValues, dataFields: DataField[]): Promise<EventBlockPutResponse> => {
    const { existingEventBlock, selectedEvents, sessionCodeDataField, timezone } = updateSessionValues;
    const eventBlock = {
        item: mapSessionFormValuesToEventBlock(updateSessionValues, existingEventBlock, sessionCodeDataField, timezone, dataFields),
        children: selectedEvents,
    };
    checkForNecessarySubEventChanges(eventBlock, existingEventBlock);
    const res = await updateEventBlock(existingEventBlock, eventBlock, eventBlock.item.id);
    return res;
};
